import { useState } from 'react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'
import LookupSelect from '../components/LookupSelect'

interface NotaAlterarData {
  empcod: number; serie: string; numero: number; cliCod: string; cliNom: string
  modelo: string; chave: string | null
  dataEmissao: string | null; dataRecebimento: string | null
  valorTotal: number
  bloqueada: boolean
  motivoBloqueio?: string
  periodoEmissaoDe: string | null; periodoEmissaoAte: string | null
  periodoRecebimentoDe: string | null; periodoRecebimentoAte: string | null
  periodoContabilDe: string | null; periodoContabilAte: string | null
}

interface NotaListaItem {
  empcod: number; serie: string; numero: number; cliCod: string; cliNom: string
  modelo: string; dataEmissao: string | null; dataRecebimento: string | null; valorTotal: number
}

function fmtMoeda(v: number) {
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
function fmtDataBr(v: string | null) {
  if (!v) return '  /  /  '
  return v.slice(0, 10).split('-').reverse().join('/')
}

const inp: React.CSSProperties = {
  border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
  fontSize: '0.85rem', height: 32, background: '#fff',
}
const label: React.CSSProperties = { fontSize: '0.72rem', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }

function Campo({ rotulo, width, children }: { rotulo: string; width?: number; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width }}>
      <label style={label}>{rotulo}</label>
      {children}
    </div>
  )
}

export default function AlterarDataNotaFiscal() {
  const { empresaCod } = useEmpresa()
  const [chave, setChave] = useState('')
  const [serie, setSerie] = useState('')
  const [numero, setNumero] = useState('')
  const [fornecedorCod, setFornecedorCod] = useState('')
  const [fornecedorDes, setFornecedorDes] = useState('')

  const [buscando, setBuscando] = useState(false)
  const [erro, setErro] = useState('')
  const [lista, setLista] = useState<NotaListaItem[] | null>(null)
  const [nota, setNota] = useState<NotaAlterarData | null>(null)
  const [novaEmissao, setNovaEmissao] = useState('')
  const [novoRecebimento, setNovoRecebimento] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [sucesso, setSucesso] = useState('')

  async function buscarDetalhe(chaveOuIdent: { chave?: string; empcod?: number; serie?: string; numero?: number; cliCod?: string }) {
    const r = await api.get('/erp/alterar-data-nota', { params: chaveOuIdent })
    setNota(r.data)
    setLista(null)
  }

  async function buscar() {
    setErro(''); setSucesso(''); setNota(null); setLista(null); setNovaEmissao(''); setNovoRecebimento('')
    const chaveDigitos = chave.replace(/\D/g, '')
    if (!chaveDigitos && !serie.trim() && !numero.trim() && !fornecedorCod) {
      setErro('Informe a Chave NF-e/CT-e, ou ao menos Série, Número ou Fornecedor.')
      return
    }
    setBuscando(true)
    try {
      if (chaveDigitos) {
        await buscarDetalhe({ chave: chaveDigitos })
        return
      }
      // Busca ampla: só o número lista todas as notas com esse número (em
      // séries/fornecedores diferentes); só o fornecedor lista todas as dele.
      const params: Record<string, string | number> = { empcod: empresaCod ?? 1 }
      if (serie.trim()) params.serie = serie.trim()
      if (numero.trim()) params.numero = Number(numero)
      if (fornecedorCod) params.cliCod = fornecedorCod
      const r = await api.get('/erp/alterar-data-nota/lista', { params })
      const encontradas: NotaListaItem[] = r.data
      if (encontradas.length === 0) {
        setErro('Nenhuma nota encontrada.')
      } else if (encontradas.length === 1) {
        const n = encontradas[0]
        await buscarDetalhe({ empcod: n.empcod, serie: n.serie, numero: n.numero, cliCod: n.cliCod })
      } else {
        setLista(encontradas)
      }
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao buscar nota(s).')
    } finally {
      setBuscando(false)
    }
  }

  async function selecionarDaLista(item: NotaListaItem) {
    setErro(''); setBuscando(true)
    try {
      await buscarDetalhe({ empcod: item.empcod, serie: item.serie, numero: item.numero, cliCod: item.cliCod })
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao buscar a nota.')
    } finally {
      setBuscando(false)
    }
  }

  async function confirmarAlteracao() {
    if (!nota) return
    if (!novaEmissao && !novoRecebimento) {
      setErro('Informe a nova data de emissão ou de recebimento.')
      return
    }
    setSalvando(true); setErro('')
    try {
      await api.post('/erp/alterar-data-nota', {
        empcod: nota.empcod, serie: nota.serie, numero: nota.numero, cliCod: nota.cliCod,
        novaEmissao: novaEmissao || undefined, novoRecebimento: novoRecebimento || undefined,
      })
      setSucesso(`Data da nota ${nota.serie}/${nota.numero} alterada com sucesso.`)
      setNota(null); setLista(null); setNovaEmissao(''); setNovoRecebimento('')
      setChave(''); setSerie(''); setNumero(''); setFornecedorCod(''); setFornecedorDes('')
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao alterar a data da nota.')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div>
      <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: '0 0 0.75rem' }}>Alterar Data de Emissão/Recebimento da Nota Fiscal</h2>

      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem', marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '0.9rem' }}>
          <Campo rotulo="Chave NF-e/CT-e" width={420}>
            <input style={{ ...inp, fontFamily: 'monospace' }} value={chave} maxLength={44}
              onChange={e => setChave(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && buscar()}
              placeholder="44 dígitos" />
          </Campo>
        </div>

        <div style={{ fontSize: '0.72rem', color: '#9ca3af', margin: '-0.5rem 0 0.9rem' }}>— ou —</div>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Campo rotulo="Série" width={90}>
            <input style={inp} value={serie} onChange={e => setSerie(e.target.value)} />
          </Campo>
          <Campo rotulo="Número" width={130}>
            <input style={inp} inputMode="numeric" value={numero} onChange={e => setNumero(e.target.value.replace(/\D/g, ''))}
              onKeyDown={e => e.key === 'Enter' && buscar()} />
          </Campo>
          <Campo rotulo="Fornecedor (opcional)" width={340}>
            <LookupSelect
              endpoint="/erp/clientes/lista" codKey="cli_cod" descKey="cli_fan"
              codValue={fornecedorCod} descValue={fornecedorDes}
              onSelect={(cod, des) => { setFornecedorCod(cod); setFornecedorDes(des) }}
              placeholder="Buscar fornecedor..." listWidth={320} somenteComTexto
            />
          </Campo>
          <button className="btn btn-primary btn-sm" disabled={buscando} onClick={buscar} style={{ height: 32 }}>
            {buscando ? 'Buscando...' : 'Buscar'}
          </button>
        </div>
      </div>

      {erro && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erro}</div>}
      {sucesso && <div className="alert alert-success" style={{ marginBottom: '0.75rem' }}>✓ {sucesso}</div>}

      {lista && (
        <div style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem', marginBottom: '1rem',
        }}>
          <div style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '0.6rem' }}>
            {lista.length} nota(s) encontrada(s) — clique na linha certa:
          </div>
          <table className="table" style={{ width: '100%', fontSize: '0.82rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'center', width: 60 }}>Série</th>
                <th style={{ textAlign: 'center', width: 90 }}>Número</th>
                <th style={{ textAlign: 'left' }}>Fornecedor</th>
                <th style={{ textAlign: 'center', width: 70 }}>Modelo</th>
                <th style={{ textAlign: 'center', width: 100 }}>Emissão</th>
                <th style={{ textAlign: 'center', width: 100 }}>Recebimento</th>
                <th style={{ textAlign: 'right', width: 120 }}>Valor</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((it, idx) => (
                <tr key={`${it.empcod}-${it.serie}-${it.numero}-${it.cliCod}`}
                  onClick={() => selecionarDaLista(it)}
                  style={{ cursor: 'pointer', background: idx % 2 === 1 ? '#f8fafc' : '#fff' }}>
                  <td style={{ textAlign: 'center' }}>{it.serie}</td>
                  <td style={{ textAlign: 'center' }}>{it.numero}</td>
                  <td>{it.cliNom} ({it.cliCod})</td>
                  <td style={{ textAlign: 'center' }}>{it.modelo}</td>
                  <td style={{ textAlign: 'center' }}>{fmtDataBr(it.dataEmissao)}</td>
                  <td style={{ textAlign: 'center' }}>{fmtDataBr(it.dataRecebimento)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: '#1a6ea8' }}>{fmtMoeda(it.valorTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {nota && (
        <div style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem',
        }}>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1rem', fontSize: '0.85rem' }}>
            <div><strong>Estab.:</strong> {nota.empcod} &nbsp; <strong>Série:</strong> {nota.serie} &nbsp; <strong>Número:</strong> {nota.numero}</div>
            <div><strong>Fornecedor:</strong> {nota.cliNom} ({nota.cliCod})</div>
            <div><strong>Modelo:</strong> {nota.modelo}</div>
            <div><strong>Valor:</strong> R$ {fmtMoeda(nota.valorTotal)}</div>
          </div>

          <div style={{
            background: '#f7faff', border: '1px solid #eef0f4', borderLeft: '4px solid #1a6ea8',
            borderRadius: 6, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.78rem', color: '#374151', lineHeight: 1.6,
          }}>
            <div>Período p/ Lançamentos Contábeis: <strong>{fmtDataBr(nota.periodoContabilDe)} à {fmtDataBr(nota.periodoContabilAte)}</strong></div>
            <div>Período Permitido p/ Emissão de NFs: <strong>{fmtDataBr(nota.periodoEmissaoDe)} à {fmtDataBr(nota.periodoEmissaoAte)}</strong></div>
            <div>Período Permitido p/ Recebimento: <strong>{fmtDataBr(nota.periodoRecebimentoDe)} à {fmtDataBr(nota.periodoRecebimentoAte)}</strong></div>
          </div>

          {nota.bloqueada ? (
            <div className="alert alert-danger">⚠ {nota.motivoBloqueio}</div>
          ) : (
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <Campo rotulo="Nova Data de Emissão" width={170}>
                <input style={inp} type="date" value={novaEmissao} onChange={e => setNovaEmissao(e.target.value)} />
                <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>Atual: {fmtDataBr(nota.dataEmissao)}</span>
              </Campo>
              <Campo rotulo="Nova Data de Recebimento" width={170}>
                <input style={inp} type="date" value={novoRecebimento} onChange={e => setNovoRecebimento(e.target.value)} />
                <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>Atual: {fmtDataBr(nota.dataRecebimento)}</span>
              </Campo>
              <button className="btn btn-primary btn-sm" disabled={salvando} onClick={confirmarAlteracao} style={{ height: 32 }}>
                {salvando ? 'Salvando...' : 'Confirmar Alteração'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
